
import React, { createContext, useContext, useEffect } from 'react';

// Always use dark theme
type Theme = 'dark';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Provider component locked to dark mode
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Always apply dark theme
  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.documentElement.classList.remove('light');
    document.body.classList.remove('light-theme');
    
    // Set data attribute for CSS targeting
    document.documentElement.setAttribute('data-theme', 'dark');
    
    // Apply font optimization for dark mode
    document.documentElement.style.setProperty('font-smooth', 'auto');
    document.documentElement.style.setProperty('-webkit-font-smoothing', 'subpixel-antialiased');
  }, []);

  // No-op functions for compatibility
  const setTheme = () => {};
  const toggleTheme = () => {};

  return (
    <ThemeContext.Provider value={{ 
      theme: 'dark', 
      setTheme, 
      resolvedTheme: 'dark', 
      toggleTheme 
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Separate the hook as a named export
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}
